import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Alert, CircularProgress,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const empty = {
  batchDate: new Date().toISOString().split('T')[0],
  millingOrderId: '',
  orderNumber: '',
  productName: '',
  batchQtyMt: '',
  premixSupplier: '',
  premixBatchNumber: '',
  premixType: '',
  targetAdditionRateGPerMt: '',
  actualAdditionRateGPerMt: '',
  actualPremixAddedKg: '',
  ironMgPerKg: '',
  zincMgPerKg: '',
  vitaminAIuPerKg: '',
  folicAcidMcgPerKg: '',
  vitaminB1MgPerKg: '',
  operatorName: '',
  notes: '',
};

const numKeys = [
  'batchQtyMt','targetAdditionRateGPerMt','actualAdditionRateGPerMt','actualPremixAddedKg',
  'ironMgPerKg','zincMgPerKg','vitaminAIuPerKg','folicAcidMcgPerKg','vitaminB1MgPerKg',
];

const NF = ({ label, k, form, set, helper }: any) => (
  <Grid item xs={12} sm={6} md={4}>
    <TextField label={label} type="number" fullWidth size="small" value={form[k]}
      onChange={(e) => set((f: any) => ({ ...f, [k]: e.target.value }))}
      inputProps={{ step: 0.01, min: 0 }} helperText={helper} />
  </Grid>
);

const FortificationForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<any>(empty);

  useEffect(() => {
    if (!isEdit) return;
    millingApi.getFortificationRecord(id!).then((r) => {
      const f: any = { ...empty };
      Object.keys(empty).forEach((k) => { f[k] = r[k] != null ? String(r[k]) : ''; });
      setForm(f);
    }).catch(() => setError('Failed to load record')).finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload: any = { ...form };
    numKeys.forEach((k) => { payload[k] = payload[k] !== '' ? Number(payload[k]) : undefined; });
    if (!payload.millingOrderId) delete payload.millingOrderId;
    try {
      if (isEdit) await millingApi.updateFortificationRecord(id!, payload);
      else await millingApi.createFortificationRecord(payload);
      navigate('/milling/fortification');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button onClick={() => navigate('/milling/fortification')}>← Back</Button>
        <Typography variant="h5">{isEdit ? 'Edit Fortification Record' : 'New Fortification Record'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Batch Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Batch Date *" type="date" fullWidth size="small" value={form.batchDate}
                  onChange={(e) => setForm((f: any) => ({ ...f, batchDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Order Number" fullWidth size="small" value={form.orderNumber}
                  onChange={(e) => setForm((f: any) => ({ ...f, orderNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Product Name" fullWidth size="small" value={form.productName}
                  onChange={(e) => setForm((f: any) => ({ ...f, productName: e.target.value }))} />
              </Grid>
              <NF label="Batch Qty (MT)" k="batchQtyMt" form={form} set={setForm} />
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Operator Name" fullWidth size="small" value={form.operatorName}
                  onChange={(e) => setForm((f: any) => ({ ...f, operatorName: e.target.value }))} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Premix Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Premix Supplier" fullWidth size="small" value={form.premixSupplier}
                  onChange={(e) => setForm((f: any) => ({ ...f, premixSupplier: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Premix Batch #" fullWidth size="small" value={form.premixBatchNumber}
                  onChange={(e) => setForm((f: any) => ({ ...f, premixBatchNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Premix Type" fullWidth size="small" value={form.premixType}
                  onChange={(e) => setForm((f: any) => ({ ...f, premixType: e.target.value }))}
                  helperText="e.g. NutriMill 900" />
              </Grid>
              <NF label="Target Addition (g/MT)" k="targetAdditionRateGPerMt" form={form} set={setForm} />
              <NF label="Actual Addition (g/MT)" k="actualAdditionRateGPerMt" form={form} set={setForm} />
              <NF label="Actual Premix Added (kg)" k="actualPremixAddedKg" form={form} set={setForm} />
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Micronutrient Levels (in finished product)</Typography>
            <Grid container spacing={2}>
              <NF label="Iron (mg/kg)" k="ironMgPerKg" form={form} set={setForm} />
              <NF label="Zinc (mg/kg)" k="zincMgPerKg" form={form} set={setForm} />
              <NF label="Vitamin A (IU/kg)" k="vitaminAIuPerKg" form={form} set={setForm} />
              <NF label="Folic Acid (mcg/kg)" k="folicAcidMcgPerKg" form={form} set={setForm} />
              <NF label="Vitamin B1 (mg/kg)" k="vitaminB1MgPerKg" form={form} set={setForm} />
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextField label="Notes" fullWidth multiline rows={2} value={form.notes}
              onChange={(e) => setForm((f: any) => ({ ...f, notes: e.target.value }))} />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Record' : 'Save Record'}
          </Button>
          <Button onClick={() => navigate('/milling/fortification')}>Cancel</Button>
        </Box>
      </form>
    </Box>
  );
};

export default FortificationForm;
